import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { apiService } from './dpopSingleton';
import { loadData } from '../utils/localStorage';

const AuthContext = createContext(null);
const HEARTBEAT_INTERVAL_MS = 60000;
const ACTIVITY_PING_ENDPOINT = '/security/activityPing';

export const AuthProvider = ({ children, toastError, toastSuccess }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const heartbeatRef = useRef({ lastPingAt: 0, isPinging: false });

  const getHistoryId = useCallback(() => {
    try {
      return JSON.parse(loadData("user") || "{}")?.history_id || null;
    } catch (error) {
      return null;
    }
  }, []);

  const pingActivity = useCallback(async (force = false) => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }

    const historyId = getHistoryId();
    if (!historyId || heartbeatRef.current.isPinging) {
      return;
    }

    const now = Date.now();
    if (!force && now - heartbeatRef.current.lastPingAt < HEARTBEAT_INTERVAL_MS) {
      return;
    }

    heartbeatRef.current.isPinging = true;
    heartbeatRef.current.lastPingAt = now;

    try {
      await apiService.authenticatedRequest(
        'POST',
        `${apiService.baseURL}${ACTIVITY_PING_ENDPOINT}`,
        { history_id: historyId },
        {},
        { showToast: false, maxRetries: 0 }
      );
    } catch (error) {
      if (error.message === 'Session expired') {
        setIsAuthenticated(false);
      }
    } finally {
      heartbeatRef.current.isPinging = false;
    }
  }, [apiService, getHistoryId]);

  useEffect(() => {
    if (!isAuthenticated || !isInitialized) {
      return undefined;
    }

    pingActivity(true);
    const intervalId = window.setInterval(() => pingActivity(false), HEARTBEAT_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        pingActivity(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, isInitialized, pingActivity]);
  useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true);
        
        // Wait for the singleton's initialization (already started on import)
        if (!apiService.dpopManager.isInitialized()) {
          await apiService.initialize();
        }
        
        if (toastError && toastSuccess) {
          apiService.setToastHandlers(toastError, toastSuccess);
        }

        apiService.setSessionExpiredCallback(() => {
          setIsAuthenticated(false);
        });
        
        if (
          apiService.tokenManager.hasTokens() &&
          !apiService.dpopManager.wasRestoredFromStorage()
        ) {
          try {
            await apiService.refreshAccessToken();
          } catch (error) {
            apiService.logout();
          }
        }

        setIsAuthenticated(apiService.isAuthenticated());
        
      } catch (error) {
        console.error('Auth initialization failed:', error);
        setInitError(error);
        setIsAuthenticated(false);
      } finally {
        setIsInitialized(true);
        setIsLoading(false);
      }
    };
    
    init();
  }, [toastError, toastSuccess]);

  const login = useCallback(async (username, password, captchaToken) => {
    try {
      const responseData = await apiService.login(username, password, captchaToken);
      setIsAuthenticated(true);
      return responseData;
    } catch (error) {
      setIsAuthenticated(false);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    const historyId = getHistoryId();
    await apiService.logoutSSO(historyId);
    heartbeatRef.current.lastPingAt = 0;
    setIsAuthenticated(false);
  }, [getHistoryId]);

  const buildUrl = useCallback((endpoint, queryParams = {}) => {
    const url = new URL(
      endpoint.startsWith('http') 
        ? endpoint 
        : `${apiService.baseURL}${endpoint}`
    );
    
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] !== undefined && queryParams[key] !== null) {
        url.searchParams.append(key, queryParams[key]);
      }
    });
    
    return url.toString();
  }, []);

  const makeRequest = useCallback(async (
    method, 
    endpoint, 
    body = null, 
    queryParams = {}, 
    extraHeaders = {},
    options = {}
  ) => {
    const fullUrl = buildUrl(endpoint, queryParams);
    
    try {
      return await apiService.authenticatedRequest(
        method, 
        fullUrl, 
        body, 
        extraHeaders, 
        options
      );
    } catch (error) {
      if (error.message === 'Session expired') {
        return null;
      }
      throw error;
    }
  }, [buildUrl]);

  const authRequest = {
    get: useCallback((endpoint, queryParams = {}, extraHeaders = {}, options = {}) =>
      makeRequest('GET', endpoint, null, queryParams, extraHeaders, options),
      [makeRequest]
    ),

    post: useCallback((endpoint, body = null, queryParams = {}, extraHeaders = {}, options = {}) =>
      makeRequest('POST', endpoint, body, queryParams, extraHeaders, options),
      [makeRequest]
    ),

    put: useCallback((endpoint, body = null, queryParams = {}, extraHeaders = {}, options = {}) =>
      makeRequest('PUT', endpoint, body, queryParams, extraHeaders, options),
      [makeRequest]
    ),

    patch: useCallback((endpoint, body = null, queryParams = {}, extraHeaders = {}, options = {}) =>
      makeRequest('PATCH', endpoint, body, queryParams, extraHeaders, options),
      [makeRequest]
    ),

    delete: useCallback((endpoint, queryParams = {}, extraHeaders = {}, options = {}) =>
      makeRequest('DELETE', endpoint, null, queryParams, extraHeaders, options),
      [makeRequest]
    ),

    getFile: useCallback(async (endpoint, queryParams = {}, extraHeaders = {}, options = {}) => {
      const fullUrl = buildUrl(endpoint, queryParams);
      
      try {
        return await apiService.downloadFile(
          'GET',
          fullUrl,
          null,
          extraHeaders,
          options
        );
      } catch (error) {
        if (error.message === 'Session expired') {
          return null;
        }
        throw error;
      }
    }, [buildUrl]),

    raw: makeRequest,
  };

  const contextValue = {
    isAuthenticated,
    isInitialized,
    isLoading,
    initError,
    login,
    logout,
    authRequest,
    apiService,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

